'use client';

import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatPrice, formatVolume } from '@/lib/format';
import { metricOptions, metricSpec, toInternal, toUi, unitSuffix, isPercentLike } from '@/lib/metrics-catalog';

/* ─────────────────────────── 类型 ─────────────────────────── */

type Operator = 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'cross_above' | 'cross_below';
type Logic = 'AND' | 'OR';
type Product = 'spot' | 'perp';
type EvalStatus = 'true' | 'false' | 'unknown';

interface Condition {
  metricId: string;
  operator: Operator;
  /** D04: 内部值（percent 类=比例，如 0.001；界面输入 0.1% 经 toInternal 存入）。 */
  value: number | '';
  valueMax?: number | '';
  window?: string;
  interval?: string;
  sourceScope?: string;
  confirmation?: string;
}

interface Expression {
  logic: Logic;
  conditions: Condition[];
}

interface RuleDto {
  id: string;
  name: string;
  description: string;
  universe: { product: Product; venue?: string } | null;
  expression: Expression | null;
  sort: string;
  columns: string[] | null;
  ruleVersion: number;
  /** D04: 1=旧百分数语义(需迁移), 2=内部比例契约。 */
  contractVersion?: number;
  createdAt: string;
  updatedAt: string;
}

interface TemplateDto {
  key: string;
  nameKey: string;
  descKey: string;
  universe: { product: Product };
  expression: Expression;
  sort: string;
}

interface EvalResultRow {
  symbol: string;
  status: EvalStatus;
  reasons: string[];
  sample: Record<string, number | null>;
  /** D15: 逐条件判定细节（侧栏展示） */
  condDetails: EvalCondDetail[];
}

/** D15: 单条件判定细节 */
interface EvalCondDetail {
  metricId: string;
  operator: string;
  threshold: number | null;
  thresholdMax: number | null;
  window?: string;
  actual: number | null;
  status: 'pass' | 'fail' | 'unknown';
  gap: number | null;
}

/** D15: 范围说明 —— 候选按流动性排序截断，非全市场 */
interface EvalUniverseMeta {
  product: Product;
  venue?: string;
  rankedBy: string;
  capLimit: number;
  note: string;
}

interface EvalResponse {
  universe: { product: Product; venue?: string };
  expression: Expression;
  logic: Logic;
  summary: { total: number; trueCount: number; falseCount: number; unknownCount: number };
  dataTime: number;
  ruleVersion: number;
  results: EvalResultRow[];
  conditionSummaries: string[];
  universeMeta?: EvalUniverseMeta;
}

type ResultFilter = 'all' | 'true' | 'false' | 'unknown';

/* ─────────────────────────── 常量 ─────────────────────────── */

const OPERATORS: Array<{ id: Operator; label: string; en?: string }> = [
  { id: 'gt', label: '>' },
  { id: 'gte', label: '≥' },
  { id: 'lt', label: '<' },
  { id: 'lte', label: '≤' },
  { id: 'between', label: '介于', en: 'between' },
  { id: 'cross_above', label: '上穿', en: 'cross above' },
  { id: 'cross_below', label: '下穿', en: 'cross below' },
];

const SORTS = ['volume', 'change', 'price', 'funding', 'oi', 'oiChange', 'rsi'];

/** D04: 表单可编辑条件值——显示界面值，存内部值。 */
function uiValue(c: Condition): number | '' {
  return c.value === '' ? '' : toUi(c.metricId, Number(c.value));
}
function onUiChange(i: number, id: string, ui: number | ''): Condition['value'] {
  return ui === '' ? '' : toInternal(id, Number(ui));
}

/** 条件人类可读摘要（D04: 界面百分数；结算周期与观测窗口分离）。 */
function condText(c: Condition, zh: boolean): string {
  const opLabel: Record<string, string> = {
    gt: '>', gte: '≥', lt: '<', lte: '≤', between: '∈', cross_above: '上穿', cross_below: '下穿',
  };
  const spec = metricSpec(c.metricId);
  const suffix = unitSuffix(c.metricId);
  const fmt = (n: number) => {
    const ui = toUi(c.metricId, n);
    return Math.abs(ui) >= 1000 ? ui.toLocaleString('en-US', { maximumFractionDigits: 0 }) : String(Number(ui.toFixed(4)));
  };
  const v = c.value === '' ? '—' : `${fmt(Number(c.value))}${suffix}`;
  const hi = c.operator === 'between' ? (c.valueMax === '' ? '—' : `${fmt(Number(c.valueMax))}${suffix}`) : null;
  const hasWin = spec != null && spec.allowedWindows.length > 0;
  const win = c.window && hasWin ? ` · ${c.window}` : '';
  const cadence = spec?.settleCadence ? ` · ${spec.settleCadence}` : '';
  const conf = c.confirmation === 'live' ? (zh ? ' · 实时' : ' · live') : '';
  const base = `${c.metricId} ${opLabel[c.operator] ?? c.operator} ${v}${hi != null ? ' ~ ' + hi : ''}`;
  return `${base}${win}${cadence}${conf}`;
}

function emptyCondition(): Condition {
  return { metricId: 'change_pct', operator: 'gte', value: 0.05, window: '24h', confirmation: 'close' };
}

/** D04: 迁移提示——条件 window 不在指标枚举 / 无窗口指标带窗口 / 旧契约 v1。 */
function conditionMigrationNotes(cond: Condition, contractVersion?: number, zh = true): string[] {
  const notes: string[] = [];
  if (contractVersion != null && contractVersion < 2) {
    notes.push(zh ? 'v1 旧规则：数值为百分数语义，需重新保存升级为 v2' : 'v1 legacy rule: values use percentage semantics, re-save to upgrade to v2');
  }
  const spec = metricSpec(cond.metricId);
  if (!spec) return notes;
  if (cond.window) {
    if (spec.allowedWindows.length && !spec.allowedWindows.includes(cond.window)) {
      notes.push(zh ? `窗口 ${cond.window} 不支援（可选 ${spec.allowedWindows.join('/')}）` : `Window ${cond.window} not supported (available: ${spec.allowedWindows.join('/')})`);
    } else if (!spec.allowedWindows.length) {
      notes.push(zh ? `观测窗口不适用（${spec.settleCadence ? `结算 ${spec.settleCadence}` : '无窗口'}）` : `Observation window N/A (${spec.settleCadence ? `settlement ${spec.settleCadence}` : 'no window'})`);
    }
  }
  return notes;
}

/* ─────────────────────────── 页面 ─────────────────────────── */

export default function ScreenerPage() {
  const { language, user } = useAppStore();
  const t = translate(language);
  const zh = language !== 'en';

  // R02: default product = spot so the default condition (change_pct ≥ 5) can
  // actually evaluate — perp samples carry change_pct: null by design.
  const [product, setProduct] = useState<Product>('spot');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logic, setLogic] = useState<Logic>('AND');
  const [conditions, setConditions] = useState<Condition[]>([emptyCondition()]);
  const [sort, setSort] = useState('volume');
  const [columns, setColumns] = useState<string[]>(['price', 'change_pct', 'quote_volume', 'funding_rate', 'open_interest']);

  // 结果
  const [evalRes, setEvalRes] = useState<EvalResponse | null>(null);
  const [evalError, setEvalError] = useState('');
  const [evaluating, setEvaluating] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);

  // 规则 CRUD
  const [rules, setRules] = useState<RuleDto[]>([]);
  const [activeRuleId, setActiveRuleId] = useState<string | null>(null);
  /** D04: 载入规则的契约版本；v1 旧规则标「需迁移」。 */
  const [activeContract, setActiveContract] = useState<number | undefined>(undefined);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [mobilePanel, setMobilePanel] = useState(false);
  const [dirty, setDirty] = useState(false);

  // D15: 结果筛选（默认仅命中）、行侧栏、批量加自选
  const [resultFilter, setResultFilter] = useState<ResultFilter>('true');
  const [detailRow, setDetailRow] = useState<EvalResultRow | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchGroupId, setBatchGroupId] = useState('');
  const [batchMsg, setBatchMsg] = useState('');
  const [addingBatch, setAddingBatch] = useState(false);

  // 滚轮锁
  useEffect(() => {
    if (mobilePanel) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobilePanel]);

  const loadRules = async () => {
    if (!user) return;
    try {
      const rows = await api.get<RuleDto[]>('/screener-rules');
      setRules(rows);
    } catch { /* ignore */ }
  };

  const loadTemplates = async () => {
    try {
      const rows = await api.get<TemplateDto[]>('/screener-rules/templates');
      setTemplates(rows);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadRules();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const markDirty = (fn: () => void) => {
    fn();
    setDirty(true);
    setPendingChanges(true);
  };

  /** 构建干净表达式(过滤空值条件)。 */
  const buildExpression = (): Expression | null => {
    const valid = conditions.filter((c) => c.metricId && c.value !== '' && c.value !== null);
    if (!valid.length) return null;
    return {
      logic,
      conditions: valid.map((c) => ({
        metricId: c.metricId,
        operator: c.operator,
        value: Number(c.value),
        ...(c.operator === 'between' && c.valueMax !== '' && c.valueMax != null ? { valueMax: Number(c.valueMax) } : {}),
        ...(c.window ? { window: c.window } : {}),
        ...(c.interval ? { interval: c.interval } : {}),
        ...(c.sourceScope ? { sourceScope: c.sourceScope } : {}),
        ...(c.confirmation ? { confirmation: c.confirmation } : {}),
      })),
    };
  };

  const validate = (): string => {
    // AUD09: unfilled rows never block the form; only filled rows are validated.
    const filled = conditions.filter((c) => c.metricId && c.value !== '' && c.value !== null);
    if (!filled.length) return zh ? '至少需要一个条件' : 'Add at least one condition';
    for (let i = 0; i < conditions.length; i++) {
      const c = conditions[i];
      if (!c.metricId || c.value === '' || c.value === null) continue;
      if (c.operator === 'between') {
        const hi = c.valueMax === '' || c.valueMax === null ? Number(c.value) : Number(c.valueMax);
        if (Number(c.value) > hi) return zh ? `条件 ${i + 1}：下限不能大于上限` : `Condition ${i + 1}: lower bound must be ≤ upper`;
      }
      if (!isFinite(Number(c.value)) ||
          (c.operator === 'between' && c.valueMax !== '' && c.valueMax != null && !isFinite(Number(c.valueMax)))) {
        return zh ? `条件 ${i + 1}：请输入有效数字` : `Condition ${i + 1}: enter a valid number`;
      }
    }
    return '';
  };

  // AUD09: live validation drives the disabled state of apply/save.
  const liveError = validate();

  const apply = async () => {
    const err = validate();
    if (err) { setEvalError(err); return; }
    const expr = buildExpression();
    if (!expr) { setEvalError(zh ? '无有效条件' : 'No valid conditions'); return; }
    setEvalError('');
    setEvaluating(true);
    try {
      const res = await api.post<EvalResponse>('/screener-rules/evaluate', {
        universe: { product },
        expression: expr,
        limit: 200,
      });
      setEvalRes(res);
      setMobilePanel(false);
      setPendingChanges(false);
    } catch (e) {
      setEvalError(e instanceof Error ? e.message : String(e));
    } finally {
      setEvaluating(false);
    }
  };

  const reset = () => {
    setConditions([emptyCondition()]);
    setLogic('AND');
    setName('');
    setDescription('');
    setActiveRuleId(null);
    setActiveContract(undefined);
    setEvalRes(null);
    setEvalError('');
    setDirty(false);
    setPendingChanges(false);
  };

  const saveRule = async () => {
    if (!user) { window.location.href = '/login?redirect=/screener'; return; }
    const nameTrim = name.trim();
    if (!nameTrim) { setSaveMsg(zh ? '请填写规则名称' : 'Enter a rule name'); return; }
    if (nameTrim.length > 40) { setSaveMsg(zh ? '名称最多 40 字符' : 'Name max 40 chars'); return; }
    if (description.length > 200) { setSaveMsg(zh ? '描述最多 200 字符' : 'Description max 200 chars'); return; }
    const err = validate();
    if (err) { setSaveMsg(err); return; }
    const expr = buildExpression();
    if (!expr) return;
    setSaving(true);
    setSaveMsg('');
    try {
      if (activeRuleId) {
        await api.put<RuleDto>(`/screener-rules/${activeRuleId}`, {
          name: nameTrim, description, universe: { product }, expression: expr, sort, columns,
        });
      } else {
        await api.post<RuleDto>('/screener-rules', {
          name: nameTrim, description, universe: { product }, expression: expr, sort, columns,
        });
      }
      setSaveMsg(zh ? '已保存 ✓' : 'Saved ✓');
      setPendingChanges(false);
      setTimeout(() => setSaveMsg(''), 2500);
      await loadRules();
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const applyRule = (r: RuleDto) => {
    if (!r.expression) return;
    const isV1 = (r.contractVersion ?? 1) < 2;
    setProduct(r.universe?.product ?? 'spot');
    setLogic(r.expression.logic);
    // D04: v1 旧规则按「界面百分数」预览换算为 v2 比例（5 → 0.05），不静默写库。
    const toV2 = (id: string, n: number) => {
      const spec = metricSpec(id);
      return spec && spec.inputScale > 1 && Math.abs(n) > 1 ? n / spec.inputScale : n;
    };
    setConditions(r.expression.conditions.map((c) => ({
      metricId: c.metricId,
      operator: c.operator,
      value: toV2(c.metricId, Number(c.value)),
      ...(c.valueMax != null ? { valueMax: toV2(c.metricId, Number(c.valueMax)) } : {}),
      ...(c.window ? { window: c.window } : {}),
      ...(c.interval ? { interval: c.interval } : {}),
      ...(c.sourceScope ? { sourceScope: c.sourceScope } : {}),
      ...(c.confirmation ? { confirmation: c.confirmation } : {}),
    })));
    setName(r.name);
    setDescription(r.description || '');
    setSort(r.sort || 'volume');
    if (r.columns && r.columns.length) setColumns(r.columns);
    setActiveRuleId(r.id);
    setActiveContract(isV1 ? 1 : 2);
    setRuleOpen(false);
    setPendingChanges(true);
  };

  const deleteRule = async (id: string) => {
    try {
      await api.delete(`/screener-rules/${id}`);
      setRules((p) => p.filter((x) => x.id !== id));
      if (activeRuleId === id) { setActiveRuleId(null); setName(''); setDescription(''); }
    } catch { /* ignore */ }
  };

  const applyTemplate = (tpl: TemplateDto) => {
    setProduct(tpl.universe.product);
    setLogic(tpl.expression.logic);
    setConditions(tpl.expression.conditions.map((c) => ({
      metricId: c.metricId,
      operator: c.operator,
      value: c.value,
      ...(c.valueMax != null ? { valueMax: c.valueMax } : {}),
      ...(c.window ? { window: c.window } : {}),
      ...(c.interval ? { interval: c.interval } : {}),
      ...(c.sourceScope ? { sourceScope: c.sourceScope } : {}),
      ...(c.confirmation ? { confirmation: c.confirmation } : {}),
    })));
    setName(zh ? tplName(tpl) : tplName(tpl));
    setDescription('');
    setSort(tpl.sort || 'volume');
    setActiveRuleId(null);
    setActiveContract(2);
    setTemplateOpen(false);
    setPendingChanges(true);
  };

  const tplName = (tpl: TemplateDto): string => {
    const map: Record<string, string> = {
      spot_relative_strength: zh ? '现货相对强势' : 'Spot relative strength',
      high_volume_rise: zh ? '高成交额上涨' : 'High volume rise',
      perp_oi_surge: zh ? '合约 OI 异动' : 'Perp OI surge',
      funding_above_threshold: zh ? '费率高于阈值' : 'Funding above threshold',
    };
    return map[tpl.key] ?? tpl.key;
  };

  const tplDesc = (tpl: TemplateDto): string => {
    const map: Record<string, string> = {
      spot_relative_strength: zh ? '24H 涨幅 ≥ 5% 且成交额 ≥ $10M' : '24h change ≥ 5% and volume ≥ $10M',
      high_volume_rise: zh ? '成交额 ≥ $20M 且 24H 涨幅 ≥ 3%（绝对阈值，非相对放量）' : 'Volume ≥ $20M and 24h change ≥ 3% (absolute thresholds)',
      perp_oi_surge: zh ? 'OI 24H 变化 ≥ 20%，费率接近 0（-0.1%~0.1%，8h 结算）' : 'OI change ≥ 20%, funding near 0 (8h settle)',
      funding_above_threshold: zh ? '资金费率 ≥ 0.05%（绝对阈值，非历史高位）' : 'Funding rate ≥ 0.05% (absolute threshold)',
    };
    return map[tpl.key] ?? tpl.descKey;
  };

  /** D15: 未知原因具体化 —— 内部字段名 → 用户可懂的原因（双语） */
  const reasonText = (raw: string, zh: boolean): string => {
    const map: Record<string, { zh: string; en: string }> = {
      oi_change_pct: { zh: '缺少 24 小时前 OI 样本', en: 'Missing 24h-ago OI sample' },
      change_pct: { zh: '缺少 24H 涨跌样本', en: 'Missing 24H change sample' },
      quote_volume: { zh: '缺少 24H 成交额样本', en: 'Missing 24H volume sample' },
      funding_rate: { zh: '缺少资金费率样本', en: 'Missing funding rate sample' },
      open_interest: { zh: '缺少持仓量样本', en: 'Missing OI sample' },
      lsr: { zh: '缺少多空比样本', en: 'Missing LSR sample' },
      rsi: { zh: '缺少 RSI 样本', en: 'Missing RSI sample' },
      price: { zh: '缺少价格样本', en: 'Missing price sample' },
      volatility: { zh: '缺少波动率样本', en: 'Missing volatility sample' },
      hv: { zh: '缺少历史波动率样本', en: 'Missing historical volatility sample' },
      market_breadth: { zh: '缺少市场宽度样本', en: 'Missing market breadth sample' },
      liq_total: { zh: '缺少爆仓总额样本', en: 'Missing total liquidation sample' },
      trade_value: { zh: '缺少成交额样本', en: 'Missing trade value sample' },
      basis_pct: { zh: '缺少基差样本', en: 'Missing basis sample' },
      leverage: { zh: '缺少杠杆样本', en: 'Missing leverage sample' },
      depth_drop_pct: { zh: '缺少深度变化样本', en: 'Missing depth change sample' },
      prev_rate: { zh: '缺少上期费率样本', en: 'Missing previous rate sample' },
      hist_prev: { zh: '缺少历史分位前值样本', en: 'Missing historical percentile (prev) sample' },
      hist_cur: { zh: '缺少历史分位现值样本', en: 'Missing historical percentile (cur) sample' },
      macd_prev: { zh: '缺少 MACD 前值样本', en: 'Missing MACD prev sample' },
      liq_long: { zh: '缺少爆仓多单样本', en: 'Missing long liquidation sample' },
      liq_short: { zh: '缺少爆仓空单样本', en: 'Missing short liquidation sample' },
      liq_count: { zh: '缺少爆仓笔数样本', en: 'Missing liquidation count sample' },
      dist_pct: { zh: '缺少距离样本', en: 'Missing distance sample' },
      liq_value: { zh: '缺少清算值样本', en: 'Missing liquidation value sample' },
      price_change_pct: { zh: '缺少价格变动样本', en: 'Missing price change sample' },
      volume_ratio: { zh: '缺少量比样本', en: 'Missing volume ratio sample' },
    };
    const m = raw.match(/缺少\s+(.+)$/);
    if (!m) return raw;
    const id = m[1].trim();
    if (map[id]) return zh ? map[id].zh : map[id].en;
    const spec = metricSpec(id);
    return spec ? `${zh ? '缺少' : 'Missing'}: ${spec.label}` : raw;
  };

  /** D15: 条件细节 —— 实际值 / 比较符 / 阈值 / 窗口 / 通过状态 / 距阈值 */
  const condDetailText = (d: EvalCondDetail, zh: boolean): string => {
    const spec = metricSpec(d.metricId);
    const label = spec?.label ?? d.metricId;
    const suf = unitSuffix(d.metricId);
    const fmt = (n: number | null) => {
      if (n == null) return '—';
      const ui = toUi(d.metricId, n);
      return Math.abs(ui) >= 1000 ? ui.toLocaleString('en-US', { maximumFractionDigits: 0 }) : String(Number(ui.toFixed(4)));
    };
    const op = d.operator === 'gt' ? '>' : d.operator === 'gte' ? '≥' : d.operator === 'lt' ? '<' : d.operator === 'lte' ? '≤' : d.operator === 'between' ? '∈' : d.operator === 'cross_above' ? '上穿' : '下穿';
    const win = d.window ? ` · ${d.window}` : '';
    const th = d.threshold == null ? '—' : `${fmt(d.threshold)}${suf}${d.thresholdMax != null ? ` ~ ${fmt(d.thresholdMax)}${suf}` : ''}`;
    const gap = d.gap == null ? '' : ` · ${d.gap >= 0 ? '+' : ''}${fmt(Math.abs(d.gap))}${suf}${zh ? ' 距阈值' : ' from thr'}`;
    return `${label} ${op} ${th}${win}${gap}`;
  };

  /** D15: 导出 CSV（含范围、计算时间、单位、版本） */
  const exportCsv = () => {
    if (!evalRes) return;
    const meta = evalRes.universeMeta;
    const head = [
      `AiLumix Screener Export`,
      `Product: ${evalRes.universe.product}${meta?.venue ? ` / ${meta.venue}` : ''}`,
      `Universe: ${meta?.note ?? ''}`,
      `Data time: ${new Date(evalRes.dataTime).toISOString()}`,
      `Rule version: v${evalRes.ruleVersion} · Logic: ${evalRes.logic}`,
      `Summary: total=${evalRes.summary.total} hit=${evalRes.summary.trueCount} miss=${evalRes.summary.falseCount} unknown=${evalRes.summary.unknownCount}`,
      '',
      'symbol,status,price,24h_pct,funding_rate,key_condition,gap',
    ];
    const rows = evalRes.results.map((r) => {
      const first = r.condDetails[0];
      const keyCond = first ? condDetailText(first, false) : '';
      const gap = first?.gap != null && first.metricId ? `${toUi(first.metricId, Math.abs(first.gap))}` : '';
      const pct = r.sample.change_pct != null ? (r.sample.change_pct * 100).toFixed(2) : '';
      const fund = r.sample.funding_rate != null ? (r.sample.funding_rate * 100).toFixed(4) : '';
      return [r.symbol, r.status, r.sample.price ?? '', pct, fund, `"${keyCond.replace(/"/g, '""')}"`, gap].join(',');
    });
    const blob = new Blob([[...head, ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screener_${new Date(evalRes.dataTime).toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** D15: 批量加自选 —— 展示数量与重复项，提交前确认 */
  const watchGroups = async (): Promise<Array<{ id: string; name: string }>> => {
    try {
      const res = await api.get<Array<{ id: string; name: string; items?: Array<{ symbol: string }> }>>('/watchlist');
      return (res ?? []).map((g) => ({ id: g.id, name: g.name }));
    } catch {
      return [];
    }
  };
  const [groupsForBatch, setGroupsForBatch] = useState<Array<{ id: string; name: string }>>([]);

  const openBatch = async () => {
    if (!user) { window.location.href = '/login?redirect=/screener'; return; }
    const gs = await watchGroups();
    setGroupsForBatch(gs);
    setBatchGroupId(gs[0]?.id ?? '');
    setBatchMsg('');
    setBatchOpen(true);
  };

  const submitBatch = async () => {
    if (!batchGroupId) { setBatchMsg(zh ? '请选择自选组' : 'Pick a group'); return; }
    if (selectedRows.size === 0) { setBatchMsg(zh ? '未选择标的' : 'Nothing selected'); return; }
    setAddingBatch(true);
    setBatchMsg('');
    try {
      let ok = 0, dup = 0;
      for (const sym of selectedRows) {
        try {
          await api.post(`/watchlist/groups/${batchGroupId}/items`, { symbol: sym });
          ok++;
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          if (msg.includes('duplicate') || msg.includes('已存在') || msg.includes('409') || msg.includes('EXISTS')) dup++;
          else throw e;
        }
      }
      setBatchMsg(zh ? `已添加 ${ok} 项${dup ? `，${dup} 项已存在` : ''} ✓` : `Added ${ok}${dup ? `, ${dup} duplicates` : ''} ✓`);
      setSelectedRows(new Set());
    } catch (e) {
      setBatchMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setAddingBatch(false);
    }
  };

  const setCond = (i: number, patch: Partial<Condition>) => {
    markDirty(() => {
      setConditions((p) => p.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
    });
  };

  const addCond = () => {
    if (conditions.length >= 10) return;
    markDirty(() => setConditions((p) => [...p, emptyCondition()]));
  };

  const removeCond = (i: number) => {
    markDirty(() => setConditions((p) => p.filter((_, idx) => idx !== i)));
  };

  /** 条件编辑器(桌面左栏 + 手机全屏共用) */
  const condPanel = (
    <div className="space-y-3">
      {/* D04: v1 旧规则迁移横幅（不静默改库，重新保存时升级） */}
      {activeContract != null && activeContract < 2 && (
        <div className="rounded-md border border-gf-negative/40 bg-gf-negative/10 px-2.5 py-1.5 text-[11px] text-gf-negative">
          ⚠ {zh ? 'v1 旧规则：已按「界面百分数」预览换算（5 = 5% → 0.05）。重新「保存」后升级为 v2 比例契约，执行与展示一致；原版本保留。' : 'v1 legacy rule: values previewed as UI percent (5 = 5% → 0.05). Re-save to upgrade to v2 ratio contract; original version preserved.'}
        </div>
      )}
      {/* 名称 / 描述 */}
      <div>
        <label className="mb-1 block text-[11px] text-gf-muted">
          {zh ? '规则名称' : 'Rule name'} <span className="text-gf-muted/60">{zh ? '(可选)' : '(optional)'}</span>
        </label>
        <input
          value={name}
          onChange={(e) => { setName(e.target.value); setPendingChanges(true); }}
          placeholder={zh ? '例如：高波动成长币' : 'e.g. High-vol momentum'}
          maxLength={40}
          className="input !py-1.5 text-xs"
        />
      </div>
      <div>
        <label className="mb-1 block text-[11px] text-gf-muted">{zh ? '描述' : 'Description'} <span className="text-gf-muted/60">(≤200)</span></label>
        <input
          value={description}
          onChange={(e) => { setDescription(e.target.value); setPendingChanges(true); }}
          placeholder={zh ? '可选备注' : 'Optional note'}
          maxLength={200}
          className="input !py-1.5 text-xs"
        />
      </div>

      {/* 逻辑切换 */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gf-muted">{zh ? '条件逻辑' : 'Logic'}</span>
        <div className="flex overflow-hidden rounded-md border border-gf-border">
          {(['AND', 'OR'] as Logic[]).map((lg) => (
            <button
              key={lg}
              onClick={() => markDirty(() => setLogic(lg))}
              className={`px-3 py-1 text-xs font-medium ${logic === lg ? 'bg-gf-accent/20 text-gf-accent' : 'text-gf-muted hover:text-gf-text'}`}
            >
              {lg === 'AND' ? (zh ? '全部满足' : 'ALL') : (zh ? '任一满足' : 'ANY')}
            </button>
          ))}
        </div>
        <span className="ml-auto text-[10px] text-gf-muted">{conditions.length}/10</span>
      </div>

      {/* 条件列表 */}
      <div className="space-y-2">
        {conditions.map((c, i) => (
          <div key={i} className="rounded-lg border border-gf-border/70 bg-gf-surface2/50 p-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[10px] font-semibold text-gf-muted">
                {zh ? `条件 ${i + 1}` : `Condition ${i + 1}`}
              </span>
              <div className="flex items-center gap-1">
                <span className="rounded bg-gf-border/40 px-1 py-0.5 text-[9px] text-gf-muted">
                  {condText(c, zh)}
                </span>
                {conditions.length > 1 && (
                  <button
                    onClick={() => removeCond(i)}
                    className="rounded px-1 text-[10px] text-gf-muted hover:bg-gf-negative/15 hover:text-gf-negative"
                    title={zh ? '删除条件' : 'Remove'}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <select
                value={c.metricId}
                onChange={(e) => {
                  const next = e.target.value;
                  // D04: 切换指标时清掉不适用窗口，值转为新指标单位域
                  const spec = metricSpec(next);
                  const win = c.window && spec?.allowedWindows.includes(c.window) ? c.window : spec?.allowedWindows[0];
                  setCond(i, { metricId: next, window: spec && spec.allowedWindows.length ? win : undefined, value: c.value === '' ? '' : toInternal(next, toUi(c.metricId, Number(c.value))) });
                }}
                aria-label={zh ? `条件 ${i + 1} 指标` : `Condition ${i + 1} metric`}
                className="input col-span-2 !py-1 text-[11px]"
              >
                {metricOptions(zh).map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
              {/* D04: 迁移提示（v1 旧规则 / 窗口不支援 / 无窗口指标带窗口） */}
              {conditionMigrationNotes(c, activeContract, zh).map((n) => (
                <span key={n} className="col-span-2 rounded bg-gf-negative/10 px-1.5 py-0.5 text-[9px] text-gf-negative">
                  ⚠ {n}
                </span>
              ))}
              {/* AUD09: between gets two equal-width inputs side by side;
                  otherwise operator + single value share one row */}
              {c.operator === 'between' ? (
                <>
                  <select
                    value={c.operator}
                    onChange={(e) => setCond(i, { operator: e.target.value as Operator })}
                    aria-label={zh ? `条件 ${i + 1} 操作符` : `Condition ${i + 1} operator`}
                    className="input col-span-2 !py-1 text-[11px]"
                  >
                    {OPERATORS.map((o) => (
                      <option key={o.id} value={o.id}>{o.en && !zh ? o.en : o.label}</option>
                    ))}
                  </select>
                  <div className="relative">
                    <input
                      type="number"
                      step={isPercentLike(c.metricId) ? 0.01 : 'any'}
                      value={uiValue(c)}
                      onChange={(e) => setCond(i, { value: e.target.value === '' ? '' : onUiChange(i, c.metricId, e.target.value === '' ? '' : Number(e.target.value)) })}
                      placeholder={zh ? '最小值' : 'Min'}
                      aria-label={zh ? `条件 ${i + 1} 最小值` : `Condition ${i + 1} min`}
                      className="input !py-1 pr-7 font-mono text-[11px]"
                    />
                    <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-gf-muted">
                      {unitSuffix(c.metricId)}
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      step={isPercentLike(c.metricId) ? 0.01 : 'any'}
                      value={uiValue({ ...c, value: c.valueMax ?? '' })}
                      onChange={(e) => setCond(i, { valueMax: e.target.value === '' ? '' : onUiChange(i, c.metricId, Number(e.target.value)) })}
                      placeholder={zh ? '最大值' : 'Max'}
                      aria-label={zh ? `条件 ${i + 1} 最大值` : `Condition ${i + 1} max`}
                      className="input !py-1 pr-7 font-mono text-[11px]"
                    />
                    <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-gf-muted">
                      {unitSuffix(c.metricId)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <select
                    value={c.operator}
                    onChange={(e) => setCond(i, { operator: e.target.value as Operator })}
                    aria-label={zh ? `条件 ${i + 1} 操作符` : `Condition ${i + 1} operator`}
                    className="input !py-1 text-[11px]"
                  >
                    {OPERATORS.map((o) => (
                      <option key={o.id} value={o.id}>{o.en && !zh ? o.en : o.label}</option>
                    ))}
                  </select>
                  <div className="relative">
                    <input
                      type="number"
                      step={isPercentLike(c.metricId) ? 0.01 : 'any'}
                      value={uiValue(c)}
                      onChange={(e) => setCond(i, { value: e.target.value === '' ? '' : onUiChange(i, c.metricId, Number(e.target.value)) })}
                      placeholder={zh ? '数值' : 'Value'}
                      aria-label={zh ? `条件 ${i + 1} 数值` : `Condition ${i + 1} value`}
                      className="input !py-1 pr-7 font-mono text-[11px]"
                    />
                    <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-gf-muted">
                      {unitSuffix(c.metricId)}
                    </span>
                  </div>
                </>
              )}
              {/* D04: 观测窗口——仅当指标允许窗口时显示；否则显示结算周期/无窗口 */}
              {(() => {
                const spec = metricSpec(c.metricId);
                if (spec && spec.allowedWindows.length > 0) {
                  return (
                    <select
                      value={c.window ?? spec.allowedWindows[0]}
                      onChange={(e) => setCond(i, { window: e.target.value })}
                      className="input !py-1 text-[11px]"
                      title={zh ? '观察窗口' : 'Window'}
                    >
                      {spec.allowedWindows.map((w) => (
                        <option key={w} value={w}>{zh ? '窗口' : 'Win'} {w}</option>
                      ))}
                    </select>
                  );
                }
                return (
                  <span className="flex items-center rounded border border-gf-border/60 px-1.5 text-[10px] text-gf-muted">
                    {spec?.settleCadence ? (zh ? `结算 ${spec.settleCadence}` : `${spec.settleCadence} settle`) : (zh ? '无窗口' : 'No window')}
                  </span>
                );
              })()}
              <select
                value={c.confirmation ?? 'close'}
                onChange={(e) => setCond(i, { confirmation: e.target.value })}
                className="input !py-1 text-[11px]"
                title={zh ? '确认方式' : 'Confirmation'}
              >
                <option value="close">{zh ? '收盘确认' : 'Close confirm'}</option>
                <option value="live">{zh ? '实时' : 'Live'}</option>
              </select>
              {c.operator === 'cross_above' || c.operator === 'cross_below' ? (
                <span className="col-span-2 text-[9px] text-gf-muted">
                  {zh ? '上穿/下穿需相邻有效数据：前一值 ≤ 阈值 且 当前值 > 阈值（缺数据 → 未知）' : 'Cross requires adjacent valid data: prev ≤ threshold & cur > threshold (missing → unknown)'}
                </span>
              ) : null}
            </div>
          </div>
        ))}
        <button
          onClick={addCond}
          disabled={conditions.length >= 10}
          className="w-full rounded-lg border border-dashed border-gf-border py-1.5 text-[11px] text-gf-muted transition-colors hover:border-gf-accent/50 hover:text-gf-accent disabled:cursor-not-allowed disabled:opacity-40"
        >
          + {zh ? '添加条件' : 'Add condition'} {conditions.length >= 10 ? '(10/10)' : ''}
        </button>
      </div>

      {/* 排序 */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gf-muted">{zh ? '排序' : 'Sort'}</span>
        <select
          value={sort}
          onChange={(e) => { setSort(e.target.value); setPendingChanges(true); }}
          className="input flex-1 !py-1 text-[11px]"
        >
          {SORTS.map((s) => (
            <option key={s} value={s}>{s === 'volume' ? (zh ? '成交额' : 'Volume') : s === 'change' ? (zh ? '涨跌幅' : 'Change') : s === 'price' ? (zh ? '价格' : 'Price') : s === 'funding' ? (zh ? '资金费率' : 'Funding') : s === 'oi' ? 'OI' : s === 'oiChange' ? `OI ${zh ? '变化' : 'Δ'}` : 'RSI'}</option>
          ))}
        </select>
      </div>

      {/* AUD09: live validation — disabled submit shows the concrete reason */}
      {liveError && (
        <div className="rounded-md border border-gf-negative/40 bg-gf-negative/10 px-2.5 py-1.5 text-[11px] text-gf-negative">{liveError}</div>
      )}
      {evalError && (
        <div className="rounded-md border border-gf-negative/40 bg-gf-negative/10 px-2.5 py-1.5 text-[11px] text-gf-negative">{evalError}</div>
      )}
    </div>
  );

  /** 结果摘要条 */
  const summaryBar = evalRes ? (
    <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gf-border/60 bg-gf-surface2/50 px-3 py-2 text-[11px]">
      <span className="font-semibold text-gf-text">
        {zh ? '命中' : 'Hit'}: <span className="text-gf-positive">{evalRes.summary.trueCount}</span>
        <span className="text-gf-muted">/{evalRes.summary.total}</span>
      </span>
      <span className="text-gf-negative">{zh ? '未命中' : 'Missed'}: {evalRes.summary.falseCount}</span>
      <span className="text-gf-muted">
        {zh ? '未知' : 'Unknown'}: {evalRes.summary.unknownCount}
        {evalRes.summary.unknownCount > 0 ? `（${zh ? '缺数据' : 'missing data'}）` : ''}
      </span>
      <span className="text-gf-muted">
        {zh ? '数据时间' : 'Data time'}: {new Date(evalRes.dataTime).toLocaleTimeString(language !== 'en' ? 'zh-TW' : 'en-US', { hour12: false })}
      </span>
      <span className="rounded bg-gf-border/40 px-1.5 py-0.5 text-[10px] text-gf-muted">
        v{evalRes.ruleVersion} · {evalRes.logic}
      </span>
      <button className="rounded border border-gf-border px-1.5 py-0.5 text-[10px] text-gf-muted hover:border-gf-accent hover:text-gf-accent" onClick={exportCsv}>
        {zh ? '导出 CSV' : 'Export CSV'}
      </button>
      {pendingChanges && (
        <span className="ml-auto rounded-full border border-gf-accent/40 bg-gf-accent/10 px-2 py-0.5 text-[10px] text-gf-accent">
          {zh ? '规则已修改，需重新套用' : 'Rule changed — re-apply'}
        </span>
      )}
    </div>
  ) : null;

  /** D15: 范围说明 —— 候选按什么选取、截断上限 */
  const universeBar = evalRes?.universeMeta ? (
    <div className="mb-2 rounded-lg border border-gf-accent/30 bg-gf-accent/5 px-3 py-1.5 text-[11px] text-gf-muted">
      <span className="text-gf-accent">ⓘ</span>{' '}
      {evalRes.universeMeta.note}
      {zh ? ' · 按 ' : ' · ranked by '}{evalRes.universeMeta.rankedBy}{zh ? ' 排序' : ''}
      {zh ? '；结果以状态筛选，未知≠未命中' : '；filter by status; unknown ≠ missed'}
    </div>
  ) : null;

  /** D15: 行侧栏 —— 逐条件实际值/比较符/阈值/窗口/通过状态 + 进入行情/历史验证/创建订阅 */
  const detailDrawer = detailRow ? (
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => setDetailRow(null)} />
      <div className="relative z-10 max-h-[80vh] w-full overflow-y-auto rounded-t-2xl border-t border-gf-border bg-gf-surface p-4 shadow-2xl sm:max-w-md sm:rounded-2xl sm:border">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-base font-semibold">{detailRow.symbol}</span>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
            detailRow.status === 'true' ? 'bg-gf-positive/15 text-gf-positive'
              : detailRow.status === 'false' ? 'bg-gf-negative/10 text-gf-negative' : 'bg-gf-muted/10 text-gf-muted'
          }`}>
            {detailRow.status === 'true' ? (zh ? '命中' : 'HIT') : detailRow.status === 'false' ? (zh ? '未中' : 'MISS') : (zh ? '未知' : 'N/A')}
          </span>
        </div>
        <div className="space-y-1.5">
          {detailRow.condDetails.length === 0 && (
            <div className="text-xs text-gf-muted">{zh ? '无条件细节' : 'No condition detail'}</div>
          )}
          {detailRow.condDetails.map((d, i) => {
            const spec = metricSpec(d.metricId);
            return (
              <div key={i} className={`rounded-lg border p-2.5 text-[11px] ${
                d.status === 'pass' ? 'border-gf-positive/30 bg-gf-positive/5'
                  : d.status === 'fail' ? 'border-gf-negative/30 bg-gf-negative/5'
                    : 'border-gf-border/60 bg-gf-surface2/40'
              }`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{spec?.label ?? d.metricId}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${
                    d.status === 'pass' ? 'bg-gf-positive/15 text-gf-positive'
                      : d.status === 'fail' ? 'bg-gf-negative/10 text-gf-negative' : 'bg-gf-muted/10 text-gf-muted'
                  }`}>
                    {d.status === 'pass' ? (zh ? '通过' : 'PASS') : d.status === 'fail' ? (zh ? '未过' : 'FAIL') : (zh ? '未知' : 'N/A')}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-gf-muted">
                  <span>{zh ? '实际' : 'Actual'}: <span className="font-mono text-gf-text">{d.actual != null ? fmtCond(d.metricId, d.actual) : '—'}</span></span>
                  <span>{zh ? '比较' : 'Cmp'}: <span className="text-gf-text">{d.operator === 'cross_above' ? (zh ? '上穿' : 'cross↑') : d.operator === 'cross_below' ? (zh ? '下穿' : 'cross↓') : d.operator === 'gt' ? '>' : d.operator === 'gte' ? '≥' : d.operator === 'lt' ? '<' : d.operator === 'lte' ? '≤' : '∈'}</span></span>
                  <span>{zh ? '阈值' : 'Threshold'}: <span className="font-mono text-gf-text">
                    {d.threshold != null ? fmtCond(d.metricId, d.threshold) : '—'}
                    {d.thresholdMax != null ? ` ~ ${fmtCond(d.metricId, d.thresholdMax)}` : ''}
                  </span></span>
                  {d.window && <span>{zh ? '窗口' : 'Win'}: <span className="text-gf-text">{d.window}</span></span>}
                  {d.gap != null && (
                    <span className={d.gap >= 0 ? 'text-gf-positive' : 'text-gf-negative'}>
                      {zh ? '距阈值' : 'Gap'}: {d.gap >= 0 ? '+' : ''}{fmtCond(d.metricId, d.gap)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {detailRow.reasons.length > 0 && (
          <div className="mt-2 rounded-lg bg-gf-surface2/50 p-2 text-[10px] text-gf-muted">
            {detailRow.reasons.map((r, i) => <div key={i}>⚠ {reasonText(r, zh)}</div>)}
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <a href={`/coin/${detailRow.symbol}`} className="btn !py-2 text-xs">
            {zh ? '进入行情' : 'Chart'}
          </a>
          <a href={`/research/studies?symbol=${detailRow.symbol}`} className="btn !py-2 text-xs">
            {zh ? '历史验证' : 'Backtest'}
          </a>
          <a href={`/alerts?type=screen&symbol=${detailRow.symbol}`} className="btn !py-2 text-xs">
            {zh ? '创建订阅' : 'Subscribe'}
          </a>
          <button className="btn-ghost !py-2 text-xs" onClick={() => setDetailRow(null)}>{zh ? '关闭' : 'Close'}</button>
        </div>
      </div>
    </div>
  ) : null;

  /** D15: 状态筛选 tabs + 批量栏 */
  const resultTabs = evalRes ? (
    <div className="mb-2 flex flex-wrap items-center gap-1.5">
      {([
        { id: 'true' as ResultFilter, label: zh ? '仅命中' : 'Hits', count: evalRes.summary.trueCount },
        { id: 'false' as ResultFilter, label: zh ? '未命中' : 'Missed', count: evalRes.summary.falseCount },
        { id: 'unknown' as ResultFilter, label: zh ? '数据不足' : 'Unknown', count: evalRes.summary.unknownCount },
        { id: 'all' as ResultFilter, label: zh ? '全部' : 'All', count: evalRes.summary.total },
      ]).map((f) => (
        <button
          key={f.id}
          onClick={() => setResultFilter(f.id)}
          className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
            resultFilter === f.id
              ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
              : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
          }`}
        >
          {f.label} <span className="opacity-70">{f.count}</span>
        </button>
      ))}
      <button
        className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
          selectedRows.size > 0
            ? 'border-gf-accent/50 bg-gf-accent/15 text-gf-accent'
            : 'border-gf-border/40 bg-gf-surface2/40 text-gf-muted/60'
        }`}
        disabled={selectedRows.size === 0}
        title={zh ? '勾选结果行后可批量加入自选' : 'Select rows to batch-add to watchlist'}
      >
        {selectedRows.size > 0
          ? (zh ? `批量加自选 (${selectedRows.size})` : `Batch add (${selectedRows.size})`)
          : (zh ? '批量加自选' : 'Batch add')}
      </button>
      {selectedRows.size === 0 && (
        <span className="text-[10px] text-gf-muted">{zh ? '勾选结果行后可加入' : 'select rows first'}</span>
      )}
    </div>
  ) : null;

  /** D15: 侧栏/导出共用格式化（function 声明提升，避免 JSX 定义顺序 TDZ） */
  function fmtCond(id: string, n: number): string {
    const ui = toUi(id, n);
    return Math.abs(ui) >= 1000 ? ui.toLocaleString('en-US', { maximumFractionDigits: 0 }) : String(Number(ui.toFixed(4))) + unitSuffix(id);
  }

  const resultTable = (rows: EvalResultRow[]) => (
    <div className="overflow-x-auto">
      <table className="table-base">
        <thead className="bg-gf-surface2/60">
          <tr>
            <th className="w-8">
              <input
                type="checkbox"
                aria-label={zh ? '全选' : 'Select all'}
                checked={rows.length > 0 && rows.every((r) => selectedRows.has(r.symbol))}
                onChange={(e) => {
                  const next = new Set(selectedRows);
                  if (e.target.checked) rows.forEach((r) => next.add(r.symbol));
                  else rows.forEach((r) => next.delete(r.symbol));
                  setSelectedRows(next);
                }}
              />
            </th>
            <th className="text-left">{zh ? '币种' : 'Symbol'}</th>
            <th className="text-right">{zh ? '状态' : 'Status'}</th>
            <th className="text-right">{zh ? '价格' : 'Price'}</th>
            <th className="text-right">24H %</th>
            <th className="text-right">{zh ? '资金费率' : 'Funding'}</th>
            <th className="text-left">{zh ? '关键值 / 距阈值' : 'Key value / gap'}</th>
            <th className="text-right">{zh ? '数据时间' : 'Time'}</th>
            <th className="text-right">{zh ? '操作' : 'Action'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const first = r.condDetails[0];
            const pct = r.sample.change_pct != null ? r.sample.change_pct * 100 : null;
            return (
              <tr key={r.symbol} className="cursor-pointer hover:bg-gf-surface2/40" onClick={() => setDetailRow(r)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={zh ? '选择' : 'Select'}
                    checked={selectedRows.has(r.symbol)}
                    onChange={(e) => {
                      const next = new Set(selectedRows);
                      if (e.target.checked) next.add(r.symbol);
                      else next.delete(r.symbol);
                      setSelectedRows(next);
                    }}
                  />
                </td>
                <td className="font-medium">{r.symbol}</td>
                <td className="text-right">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    r.status === 'true' ? 'bg-gf-positive/15 text-gf-positive'
                      : r.status === 'false' ? 'bg-gf-negative/10 text-gf-negative'
                        : 'bg-gf-muted/10 text-gf-muted'
                  }`}>
                    {r.status === 'true' ? (zh ? '命中' : 'HIT') : r.status === 'false' ? (zh ? '未中' : 'MISS') : (zh ? '未知' : 'N/A')}
                  </span>
                </td>
                <td className="text-right font-mono">{r.sample.price != null ? formatPrice(r.sample.price) : '—'}</td>
                <td className={`text-right font-mono ${pct != null && pct >= 0 ? 'text-gf-positive' : pct != null ? 'text-gf-negative' : 'text-gf-muted'}`}>
                  {pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : '—'}
                </td>
                <td className={`text-right font-mono ${r.sample.funding_rate != null && r.sample.funding_rate > 0 ? 'text-gf-positive' : r.sample.funding_rate != null ? 'text-gf-negative' : 'text-gf-muted'}`}>
                  {r.sample.funding_rate != null ? `${(r.sample.funding_rate * 100).toFixed(4)}%` : '—'}
                </td>
                <td className="max-w-[200px] truncate text-left text-[11px] text-gf-muted" title={first ? condDetailText(first, zh) : '—'}>
                  {first ? condDetailText(first, zh) : '—'}
                </td>
                <td className="whitespace-nowrap text-right text-[10px] text-gf-muted">
                  {new Date(evalRes?.dataTime ?? 0).toLocaleTimeString(language !== 'en' ? 'zh-TW' : 'en-US', { hour12: false })}
                </td>
                <td className="text-right">
                  <button className="text-xs text-gf-accent hover:underline" onClick={(e) => { e.stopPropagation(); setDetailRow(r); }}>
                    {zh ? '详情' : 'Detail'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const mobileCards = (rows: EvalResultRow[]) => (
    <div className="flex flex-col gap-2">
      {rows.map((r) => {
        const first = r.condDetails[0];
        return (
          <div key={r.symbol} className="card !py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  aria-label={zh ? '选择' : 'Select'}
                  checked={selectedRows.has(r.symbol)}
                  onChange={(e) => {
                    const next = new Set(selectedRows);
                    if (e.target.checked) next.add(r.symbol);
                    else next.delete(r.symbol);
                    setSelectedRows(next);
                  }}
                />
                <button className="min-w-0 flex-1 text-left" onClick={() => setDetailRow(r)}>
                  <span className="flex items-center gap-1.5">
                    <span className="font-semibold">{r.symbol}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      r.status === 'true' ? 'bg-gf-positive/15 text-gf-positive' : r.status === 'false' ? 'bg-gf-negative/10 text-gf-negative' : 'bg-gf-muted/10 text-gf-muted'
                    }`}>
                      {r.status === 'true' ? (zh ? '命中' : 'HIT') : r.status === 'false' ? (zh ? '未中' : 'MISS') : (zh ? '未知' : 'N/A')}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-[10px] text-gf-muted">
                    {new Date(evalRes?.dataTime ?? 0).toLocaleTimeString(language !== 'en' ? 'zh-TW' : 'en-US', { hour12: false })}
                  </span>
                </button>
              </div>
              <button className="shrink-0 rounded border border-gf-border px-2 py-1 text-[10px] text-gf-accent" onClick={() => setDetailRow(r)}>
                {zh ? '详情' : 'Detail'}
              </button>
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="font-mono text-base">{r.sample.price != null ? formatPrice(r.sample.price) : '—'}</span>
              {r.sample.change_pct != null && (
                <span className={`font-mono text-xs ${r.sample.change_pct >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                  {r.sample.change_pct >= 0 ? '+' : ''}{(r.sample.change_pct * 100).toFixed(2)}%
                </span>
              )}
            </div>
            {first && (
              <div className="mt-1 truncate text-[10px] text-gf-muted">{condDetailText(first, zh)}</div>
            )}
          </div>
        );
      })}
    </div>
  );

  const allRows = evalRes?.results ?? [];
  const rows = resultFilter === 'all' ? allRows : allRows.filter((r) => r.status === resultFilter);
  const hits = rows.filter((r) => r.status === 'true');

  return (
    <div>
      {/* 页头 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{zh ? '市场筛选器' : 'Market Screener'}</h1>
          <p className="mt-0.5 text-xs text-gf-muted">
            {zh ? '构建可验证的规则，跨现货 / 合约筛选标的' : 'Build verifiable rules across spot & perp universes'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* 产品切换 */}
          <div className="flex overflow-hidden rounded-lg border border-gf-border">
            {(['spot', 'perp'] as Product[]).map((p) => (
              <button
                key={p}
                onClick={() => { setProduct(p); setActiveRuleId(null); setPendingChanges(true); setEvalRes(null); }}
                className={`px-3 py-1.5 text-xs font-medium ${product === p ? 'bg-gf-accent/20 text-gf-accent' : 'text-gf-muted hover:text-gf-text'}`}
              >
                {p === 'spot' ? (zh ? '现货' : 'Spot') : (zh ? '合约' : 'Perp')}
              </button>
            ))}
          </div>
          {/* 模板 */}
          <div className="relative">
            <button
              className="btn !py-1.5 text-xs"
              onClick={() => { setTemplateOpen((v) => !v); setRuleOpen(false); }}
            >
              {zh ? '模板' : 'Templates'}
            </button>
            {templateOpen && (
              <div className="absolute right-0 top-full z-30 mt-1 w-72 rounded-lg border border-gf-border bg-[rgb(var(--gf-card))] p-1.5 shadow-xl">
                <div className="px-2 py-1 text-[10px] text-gf-muted">{zh ? '点击载入模板（可自由修改）' : 'Click to load a template (editable)'}</div>
                {templates.map((tpl) => (
                  <button
                    key={tpl.key}
                    onClick={() => applyTemplate(tpl)}
                    className="block w-full rounded-md px-2 py-1.5 text-left hover:bg-gf-surface2/60"
                  >
                    <div className="text-xs font-medium">{tplName(tpl)}</div>
                    <div className="text-[10px] text-gf-muted">{tplDesc(tpl)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* 我的规则 */}
          <div className="relative">
            <button
              className="btn !py-1.5 text-xs"
              onClick={() => { setRuleOpen((v) => !v); setTemplateOpen(false); if (user) loadRules(); }}
            >
              {language === 'zh-Hant' ? `我的規則` : zh ? `我的规则` : 'My rules'} {user ? `(${rules.length})` : ''}
            </button>
            {ruleOpen && (
              <div className="absolute right-0 top-full z-30 mt-1 w-72 rounded-lg border border-gf-border bg-[rgb(var(--gf-card))] p-1.5 shadow-xl">
                {!user ? (
                  <div className="px-2 py-2 text-[11px] text-gf-muted">
                    {language === 'zh-Hant' ? '登入後可保存規則' : zh ? '登录后可保存规则' : 'Log in to save rules'}
                  </div>
                ) : rules.length === 0 ? (
                  <div className="px-2 py-2 text-[11px] text-gf-muted">{zh ? '暂无规则' : 'No rules yet'}</div>
                ) : (
                  rules.map((r) => (
                    <div key={r.id} className="flex items-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-gf-surface2/60">
                      <button className="min-w-0 flex-1 truncate text-left text-xs hover:text-gf-accent" onClick={() => applyRule(r)}>
                        {r.name}
                        <span className="ml-1 text-[9px] text-gf-muted">v{r.ruleVersion}</span>
                      </button>
                      <button className="shrink-0 text-[10px] text-gf-muted hover:text-gf-negative" onClick={() => deleteRule(r.id)}>
                        {zh ? '删除' : 'Del'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {/* 保存 */}
          {user && (
            <button className="btn-primary !py-1.5 text-xs" onClick={saveRule} disabled={saving || !!liveError}>
              {saving ? '…' : zh ? '保存' : 'Save'}
            </button>
          )}
          {saveMsg && <span className={`text-[11px] ${saveMsg.includes('✓') ? 'text-gf-positive' : 'text-gf-negative'}`}>{saveMsg}</span>}
          {/* 手机筛选入口 */}
          <button className="btn !py-1.5 text-xs lg:hidden" onClick={() => setMobilePanel(true)}>
            {zh ? '条件' : 'Rules'}
            {conditions.length > 0 && <span className="ml-1 rounded-full bg-gf-accent/20 px-1.5 text-[10px] text-gf-accent">{conditions.length}</span>}
          </button>
        </div>
      </div>

      <div className="flex items-start gap-4">
        {/* 桌面左栏 280px */}
        <div className="card hidden w-[280px] shrink-0 !p-4 lg:block">
          {condPanel}
          <div className="mt-4 flex gap-2">
            <button className="btn-primary flex-1 !py-2 text-xs" onClick={apply} disabled={evaluating || !!liveError}>
              {evaluating ? (zh ? '计算中…' : 'Running…') : t.common.apply}
            </button>
            <button className="btn flex-1 !py-2 text-xs" onClick={reset}>{t.common.reset}</button>
          </div>
        </div>

        {/* 右侧结果 */}
        <div className="min-w-0 flex-1">
          {!evalRes ? (
            <div className="card p-8 text-center text-sm text-gf-muted">
              {zh
                ? '设置条件后点击「套用」。示例：24H 涨幅 ≥ 5% 且 成交额 ≥ $10M（现货）；OI 变化 ≥ 20% 且 费率 ∈ [-0.1%, 0.1%]（合约）。'
                : 'Set conditions and Apply. e.g. spot: 24h change ≥ 5% & volume ≥ $10M; perp: OI change ≥ 20% & funding ∈ [-0.1%, 0.1%].'}
            </div>
          ) : (
            <>
              {summaryBar}
              {universeBar}
              {resultTabs}
              {/* 规则摘要(结果首屏) */}
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-semibold">{name || (zh ? '未命名规则' : 'Untitled rule')}</span>
                {evalRes.conditionSummaries.map((cs, i) => (
                  <span key={i} className="rounded border border-gf-border/60 bg-gf-surface2/60 px-1.5 py-0.5 text-[10px] text-gf-muted">{cs}</span>
                ))}
              </div>
              {/* 桌面表格 */}
              <div className="card hidden !p-0 overflow-hidden sm:block">
                {rows.length ? resultTable(rows) : (
                  <div className="py-10 text-center text-sm text-gf-muted">
                    {allRows.length === 0 ? (zh ? '无结果' : 'No results')
                      : resultFilter === 'true' ? (zh ? '没有命中结果（切换「全部」查看）' : 'No hits (switch to All)')
                        : resultFilter === 'unknown' ? (zh ? '没有数据不足项（未知≠未命中）' : 'No unknowns (unknown ≠ missed)')
                          : (zh ? '此状态无结果' : 'No rows in this state')}
                  </div>
                )}
              </div>
              {/* 手机卡片 */}
              <div className="sm:hidden">
                {rows.length ? mobileCards(rows) : (
                  <div className="card p-6 text-center text-sm text-gf-muted">
                    {allRows.length === 0 ? (zh ? '无结果' : 'No results')
                      : resultFilter === 'true' ? (zh ? '没有命中结果（切换「全部」查看）' : 'No hits (switch to All)')
                        : resultFilter === 'unknown' ? (zh ? '没有数据不足项' : 'No unknowns')
                          : (zh ? '此状态无结果' : 'No rows in this state')}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* D15: 行侧栏 */}
      {detailDrawer}

      {/* D15: 批量加自选弹窗 */}
      {batchOpen && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setBatchOpen(false)} />
          <div className="relative z-10 w-full rounded-t-2xl border-t border-gf-border bg-gf-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl sm:border">
            <h3 className="mb-1 text-base font-semibold">{zh ? '批量加入自选' : 'Add to watchlist'}</h3>
            <div className="mb-3 text-xs text-gf-muted">
              {zh
                ? `已选 ${selectedRows.size} 项：${Array.from(selectedRows).slice(0, 8).join(', ')}${selectedRows.size > 8 ? ` +${selectedRows.size - 8}` : ''}`
                : `${selectedRows.size} selected: ${Array.from(selectedRows).slice(0, 8).join(', ')}${selectedRows.size > 8 ? ` +${selectedRows.size - 8}` : ''}`}
            </div>
            <label className="mb-1 block text-[11px] text-gf-muted">{zh ? '目标自选组' : 'Target group'}</label>
            <select
              value={batchGroupId}
              onChange={(e) => setBatchGroupId(e.target.value)}
              className="input !py-2 text-xs"
            >
              {groupsForBatch.length === 0 && <option value="">{zh ? '（暂无自选组，请先在自选页创建）' : '(no groups — create one first)'}</option>}
              {groupsForBatch.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            {batchMsg && <div className={`mt-2 text-[11px] ${batchMsg.includes('✓') ? 'text-gf-positive' : 'text-gf-negative'}`}>{batchMsg}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-ghost !h-9" onClick={() => setBatchOpen(false)}>{t.common.cancel}</button>
              <button className="btn-primary !h-9 text-xs" onClick={submitBatch} disabled={addingBatch || !batchGroupId}>
                {addingBatch ? '…' : zh ? '确认加入' : 'Confirm add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 手机全屏条件面板 */}
      {mobilePanel && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-gf-bg lg:hidden">
          <div className="flex items-center justify-between border-b border-gf-border px-4 py-3">
            <span className="text-sm font-semibold">{zh ? '条件编辑' : 'Rule editor'}</span>
            <button className="text-xs text-gf-muted hover:text-gf-text" onClick={() => setMobilePanel(false)}>
              {zh ? '关闭' : 'Close'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {condPanel}
          </div>
          <div className="flex gap-2 border-t border-gf-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <button className="btn flex-1 !py-2.5 text-sm" onClick={() => { reset(); }}>{t.common.reset}</button>
            <button className="btn-primary flex-1 !py-2.5 text-sm" onClick={apply} disabled={evaluating || !!liveError}>
              {evaluating ? (zh ? '计算中…' : 'Running…') : t.common.apply}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
